import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import { supabase } from "../lib/supabaseClient";
import type { ColumnRow } from "../lib/supabaseClient";

export interface ColumnData {
  id: number;
  title: string;
  content: string;
  date: string;
  imageUrl?: string;
  view_count?: number;
}

interface ColumnContextType {
  columns: ColumnData[];
  loading: boolean;
  error: string | null;
  getColumn: (id: number) => ColumnData | undefined;
  addColumn: (column: Omit<ColumnData, "id">) => Promise<void>;
  updateColumn: (
    id: number,
    updatedColumn: Partial<ColumnData>,
  ) => Promise<void>;
  deleteColumn: (id: number) => Promise<void>;
  refreshColumns: () => Promise<void>;
  incrementLocalViewCount: (id: number) => void;
}

const mapToColumnData = (row: ColumnRow): ColumnData => ({
  id: row.id,
  title: row.title,
  content: row.content,
  date: row.date,
  imageUrl: row.image_url || undefined,
  view_count: row.view_count,
});

export const ColumnContext = createContext<ColumnContextType | undefined>(
  undefined,
);

export const ColumnProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [columns, setColumns] = useState<ColumnData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchColumns = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("columns")
        .select("*")
        .order("date", { ascending: false });

      if (error) throw error;
      setColumns((data || []).map(mapToColumnData));
      setError(null);
    } catch (err: any) {
      setError(err.message);
      console.error("Error fetching columns:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchColumns();
  }, []);

  const getColumn = (id: number) => {
    return columns.find((column) => column.id === id);
  };

  const addColumn = async (newColumn: Omit<ColumnData, "id">) => {
    const { error } = await supabase
      .from("columns")
      .insert([
        {
          title: newColumn.title,
          content: newColumn.content,
          date: newColumn.date,
          image_url: newColumn.imageUrl?.trim() || null,
        },
      ])
      .select();

    if (error) throw error;
    await fetchColumns();
  };

  const updateColumn = async (
    id: number,
    updatedColumn: Partial<ColumnData>,
  ) => {
    const updateData: any = {};
    if (updatedColumn.title) updateData.title = updatedColumn.title;
    if (updatedColumn.content !== undefined)
      updateData.content = updatedColumn.content;
    if (updatedColumn.date) updateData.date = updatedColumn.date;
    if (updatedColumn.imageUrl !== undefined)
      updateData.image_url = updatedColumn.imageUrl.trim() || null;

    const { error } = await supabase
      .from("columns")
      .update(updateData)
      .eq("id", id);

    if (error) throw error;
    await fetchColumns();
  };

  const deleteColumn = async (id: number) => {
    const { error } = await supabase.from("columns").delete().eq("id", id);

    if (error) throw error;
    await fetchColumns();
  };

  const incrementLocalViewCount = useCallback((id: number) => {
    setColumns((prev) =>
      prev.map((c) =>
        c.id === id ? { ...c, view_count: (c.view_count || 0) + 1 } : c,
      ),
    );
  }, []);

  return (
    <ColumnContext.Provider
      value={{
        columns,
        loading,
        error,
        getColumn,
        addColumn,
        updateColumn,
        deleteColumn,
        refreshColumns: fetchColumns,
        incrementLocalViewCount,
      }}
    >
      {children}
    </ColumnContext.Provider>
  );
};
